// Aerator Project - Engineering Change Notice (ECN) Seed Data
// Complete Design Change Log for all 17 Iterations

export interface ECNRecord {
    ecnNumber: string;
    ecnTitle: string;
    ecnType: 'Design Change' | 'Material Change' | 'Process Change' | 'Supplier Change' | 'Documentation';
    priority: 'Critical' | 'High' | 'Medium' | 'Low';
    status: 'Submitted' | 'Under Review' | 'Approved' | 'Rejected' | 'Implemented' | 'Closed';

    // People & Dates
    submittedBy: string; // Initials
    submittedDate: string;
    reviewedBy: string[]; // Array of reviewer initials
    approvedBy: string; // Final approver initials
    approvedDate: string;
    implementedDate: string;
    closedDate?: string;

    // Change Details
    changeDescription: string;
    reasonForChange: string;
    affectedDocuments: string[]; // BOM, DFMEA, Drawings, etc.
    affectedItems: string[]; // Part numbers or FMEA IDs
    previousConfiguration: string;
    newConfiguration: string;

    // Impact Assessment
    costImpact: {
        tooling: number;
        material: number;
        labor: number;
        total: number;
    };
    scheduleImpact: string;
    qualityImpact: string;
    validationRequired: boolean;
    testingRequired: string[];

    // Linked Items
    linkedFmeaIds?: string[];
    linkedBomIds?: string[];
    linkedMaterialIds?: string[];
    linkedIterationNumber: number;

    // Attachments/References
    attachments?: string[];
    relatedECNs?: string[];
    dispositionOfExistingParts?: string;

    // Approval Chain
    approvalHistory: {
        reviewer: string;
        role: string;
        decision: 'Approved' | 'Rejected' | 'Conditional';
        date: string;
        comments: string;
    }[];
}

// ============================================
// ECN LOG - ALL 29 DESIGN CHANGES
// ============================================

export const ecnLog: ECNRecord[] = [
    {
        ecnNumber: 'ECN-2025-001',
        ecnTitle: 'Wire Strain Relief Design Addition',
        ecnType: 'Design Change',
        priority: 'High',
        status: 'Closed',

        submittedBy: 'WK',
        submittedDate: '2025-08-08',
        reviewedBy: ['MC', 'JS'],
        approvedBy: 'MC',
        approvedDate: '2025-08-09',
        implementedDate: '2025-08-10',
        closedDate: '2025-08-15',

        changeDescription: 'Add molded strain relief feature at wire exit points in housing to prevent flex fatigue and wire breakage under vibration.',
        reasonForChange: 'FMEA-003 identified wire break due to vibration as high risk (RPN 144). Strain relief will reduce occurrence from 4 to 2.',

        affectedDocuments: ['BOM Rev A', 'DFMEA Rev 1', 'Housing Drawing MF-HSG-001'],
        affectedItems: ['MF-WIRE-001', 'MF-HSG-001'],
        previousConfiguration: 'Wire exits housing without strain relief, vulnerable to flex fatigue',
        newConfiguration: 'Molded strain relief ribs integrated into housing exit, minimum bend radius 5mm',

        costImpact: {
            tooling: 500,
            material: 0.15,
            labor: 0,
            total: 500.15
        },
        scheduleImpact: '2 weeks for tool modification',
        qualityImpact: 'Reduces wire failure risk by 50%, RPN reduction from 144 to 72',
        validationRequired: true,
        testingRequired: ['Wire flex fatigue test', 'Pull test'],

        linkedFmeaIds: ['FMEA-003'],
        linkedBomIds: ['12'],
        linkedIterationNumber: 2,

        attachments: ['Strain_Relief_Design_Sketch.pdf', 'FMEA-003_Analysis.pdf'],
        dispositionOfExistingParts: 'Scrap 50 units without strain relief',

        approvalHistory: [
            {
                reviewer: 'MC',
                role: 'Lead Engineer',
                decision: 'Approved',
                date: '2025-08-09',
                comments: 'Good design solution. Approve for implementation.'
            },
            {
                reviewer: 'JS',
                role: 'Quality Engineer',
                decision: 'Approved',
                date: '2025-08-09',
                comments: 'Will significantly reduce wire failure risk. Approved.'
            }
        ]
    },

    {
        ecnNumber: 'ECN-2025-002',
        ecnTitle: 'Battery Contact Material Upgrade - Nickel Plating',
        ecnType: 'Material Change',
        priority: 'Medium',
        status: 'Closed',

        submittedBy: 'MC',
        submittedDate: '2025-08-18',
        reviewedBy: ['WK', 'JS'],
        approvedBy: 'WK',
        approvedDate: '2025-08-19',
        implementedDate: '2025-08-20',
        closedDate: '2025-08-25',

        changeDescription: 'Change battery contact material from bare steel to nickel-plated steel (5-10 μm nickel per ASTM B689).',
        reasonForChange: 'FMEA-004 identified battery contact corrosion as risk (RPN 140). Nickel plating provides corrosion protection in humid environments.',

        affectedDocuments: ['BOM Rev A', 'Material Spec MS-STEEL-001', 'Purchase Spec'],
        affectedItems: ['MF-BCS-001', 'MF-BCP-001'],
        previousConfiguration: 'Bare spring steel contacts, susceptible to oxidation',
        newConfiguration: 'Nickel-plated spring steel, 5-10 μm coating thickness',

        costImpact: {
            tooling: 0,
            material: 0.25,
            labor: 0,
            total: 0.25
        },
        scheduleImpact: 'No impact - supplier can provide immediately',
        qualityImpact: 'Corrosion resistance improved, extends product life',
        validationRequired: true,
        testingRequired: ['Salt spray test 48h', 'Contact resistance measurement'],

        linkedFmeaIds: ['FMEA-004'],
        linkedBomIds: ['4', '5'],
        linkedMaterialIds: ['MAT-STEEL-SPRING-001', 'MAT-STEEL-NI-001'],
        linkedIterationNumber: 3,

        attachments: ['Nickel_Plating_Spec.pdf', 'Supplier_Certification.pdf'],
        relatedECNs: [],
        dispositionOfExistingParts: 'Use up existing bare steel stock (200 pcs), then switch',

        approvalHistory: [
            {
                reviewer: 'WK',
                role: 'Lead Engineer',
                decision: 'Approved',
                date: '2025-08-19',
                comments: 'Cost increase acceptable for reliability improvement.'
            },
            {
                reviewer: 'JS',
                role: 'Quality Engineer',
                decision: 'Approved',
                date: '2025-08-19',
                comments: 'Will validate with salt spray test. Approved.'
            }
        ]
    },

    {
        ecnNumber: 'ECN-2025-003',
        ecnTitle: '304 Stainless Steel Specification for Food Contact',
        ecnType: 'Material Change',
        priority: 'Critical',
        status: 'Closed',

        submittedBy: 'WK',
        submittedDate: '2025-08-28',
        reviewedBy: ['JS', 'MC'],
        approvedBy: 'JS',
        approvedDate: '2025-08-30',
        implementedDate: '2025-09-01',
        closedDate: '2025-09-10',

        changeDescription: 'Specify 304 stainless steel per ASTM A240 for all food-contact surfaces (shaft and whisk). FDA CFR 177.2750 compliant.',
        reasonForChange: 'FDA food safety requirement. FMEA-015 identified corrosion risk (RPN 160). 304 SS is food-safe and corrosion resistant.',

        affectedDocuments: ['BOM Rev A→B', 'Material Spec MS-SS304-01', 'DFMEA Rev 1→2'],
        affectedItems: ['MF-SFT-001', 'MF-WHK-001'],
        previousConfiguration: 'Unspecified stainless steel, no FDA certification',
        newConfiguration: '304 SS per ASTM A240, FDA food contact approved, supplier certification required',

        costImpact: {
            tooling: 0,
            material: 0.80,
            labor: 0,
            total: 0.80
        },
        scheduleImpact: 'None - supplier stocks 304 SS',
        qualityImpact: 'FDA compliance achieved, eliminates corrosion failure mode',
        validationRequired: true,
        testingRequired: ['FDA certification review', 'Corrosion resistance test', 'XRF material verification'],

        linkedFmeaIds: ['FMEA-015'],
        linkedBomIds: ['13', '14'],
        linkedMaterialIds: ['MAT-SS304-001'],
        linkedIterationNumber: 4,

        attachments: ['FDA_CFR_177.2750.pdf', '304_SS_Material_Cert.pdf', 'ASTM_A240_Spec.pdf'],
        dispositionOfExistingParts: 'Scrap all non-304 SS parts (30 pcs)',

        approvalHistory: [
            {
                reviewer: 'JS',
                role: 'Quality Engineer',
                decision: 'Approved',
                date: '2025-08-30',
                comments: 'Critical for FDA compliance. Must implement immediately.'
            },
            {
                reviewer: 'MC',
                role: 'Manufacturing Engineer',
                decision: 'Approved',
                date: '2025-08-30',
                comments: 'Supplier confirmed availability. No manufacturing impact.'
            }
        ]
    },

    {
        ecnNumber: 'ECN-2025-004',
        ecnTitle: 'High-Impact ABS Housing Material Upgrade',
        ecnType: 'Material Change',
        priority: 'High',
        status: 'Closed',

        submittedBy: 'WK',
        submittedDate: '2025-09-12',
        reviewedBy: ['MC', 'JS'],
        approvedBy: 'MC',
        approvedDate: '2025-09-14',
        implementedDate: '2025-09-15',
        closedDate: '2025-09-22',

        changeDescription: 'Upgrade housing material from standard ABS to high-impact ABS grade HI-95 (ASTM D4673) with impact strength >300 J/m.',
        reasonForChange: 'Drop test failure (1m onto concrete). FMEA-001 housing crack risk (RPN 240). High-impact ABS required to pass validation.',

        affectedDocuments: ['BOM Rev B→C', 'Material Spec MS-ABS-01', 'Housing DWG Rev C'],
        affectedItems: ['MF-HSG-001', 'MF-SWC-001'],
        previousConfiguration: 'Standard ABS, impact strength 200 J/m - failed drop test',
        newConfiguration: 'High-impact ABS HI-95, impact strength 350 J/m - passes drop test',

        costImpact: {
            tooling: 0,
            material: 0.40,
            labor: 0,
            total: 0.40
        },
        scheduleImpact: '1 week for material order and validation',
        qualityImpact: 'Drop test now passes, RPN reduced from 240 to 144',
        validationRequired: true,
        testingRequired: ['Drop test 1m concrete 3-axis', 'Impact strength verification'],

        linkedFmeaIds: ['FMEA-001'],
        linkedBomIds: ['2', '9'],
        linkedMaterialIds: ['MAT-ABS-001'],
        linkedIterationNumber: 5,

        attachments: ['Drop_Test_Report_Failure.pdf', 'HI-95_Datasheet.pdf', 'Drop_Test_Pass.pdf'],
        relatedECNs: [],
        dispositionOfExistingParts: 'Scrap 100 units standard ABS housings',

        approvalHistory: [
            {
                reviewer: 'MC',
                role: 'Lead Engineer',
                decision: 'Approved',
                date: '2025-09-14',
                comments: 'Critical for validation. Cost acceptable.'
            },
            {
                reviewer: 'JS',
                role: 'Quality Engineer',
                decision: 'Approved',
                date: '2025-09-14',
                comments: 'Drop test validation mandatory. Approved to proceed.'
            }
        ]
    },

    {
        ecnNumber: 'ECN-2025-005',
        ecnTitle: 'Shaft Seal Material Change - Silicone to Viton FKM',
        ecnType: 'Material Change',
        priority: 'Critical',
        status: 'Closed',

        submittedBy: 'WK',
        submittedDate: '2025-09-22',
        reviewedBy: ['JS', 'MC'],
        approvedBy: 'JS',
        approvedDate: '2025-09-24',
        implementedDate: '2025-09-25',
        closedDate: '2025-10-05',

        changeDescription: 'Replace shaft seal from silicone rubber to Viton FKM (fluoroelastomer) A-Type, 75 Shore A, FDA approved per CFR 177.2600.',
        reasonForChange: 'CRITICAL FAILURE: Silicone seal failed hot liquid test at 80°C (steamed milk). FMEA-016 highest RPN=270. Viton rated to 200°C.',

        affectedDocuments: ['BOM Rev C→D', 'Seal Specification', 'DFMEA Rev 2→3', 'Test Protocol'],
        affectedItems: ['MF-SEL-001'],
        previousConfiguration: 'Silicone O-ring, temp limit 150°C - FAILED 80°C test',
        newConfiguration: 'Viton FKM O-ring, temp limit 200°C - PASSED 80°C + 100°C tests',

        costImpact: {
            tooling: 0,
            material: 1.20,
            labor: 0,
            total: 1.20
        },
        scheduleImpact: '3 weeks for supplier qualification and testing',
        qualityImpact: 'CRITICAL - Eliminates seal failure in hot liquids. RPN 270→162',
        validationRequired: true,
        testingRequired: ['Hot liquid immersion 80°C 30min', 'Seal leak test', 'Compression set test', 'FDA food contact cert'],

        linkedFmeaIds: ['FMEA-016'],
        linkedBomIds: ['16'],
        linkedMaterialIds: ['MAT-VITON-001'],
        linkedIterationNumber: 6,

        attachments: ['Silicone_Failure_Report.pdf', 'Viton_Hot_Test_Pass.pdf', 'FDA_CFR_177.2600.pdf', 'Seal_Leak_Test.pdf'],
        relatedECNs: ['ECN-2025-010'],
        dispositionOfExistingParts: 'SCRAP ALL silicone seals immediately (500 pcs) - do not use',

        approvalHistory: [
            {
                reviewer: 'JS',
                role: 'Quality Engineer',
                decision: 'Approved',
                date: '2025-09-24',
                comments: 'CRITICAL ISSUE. Viton is the correct solution. Fast-track approval.'
            },
            {
                reviewer: 'MC',
                role: 'Manufacturing',
                decision: 'Approved',
                date: '2025-09-24',
                comments: 'Supplier AdvancedSeals can deliver in 1 week. Approved.'
            }
        ]
    },

    {
        ecnNumber: 'ECN-2025-006',
        ecnTitle: 'Switch Gasket Material - Silicone to TPE',
        ecnType: 'Material Change',
        priority: 'High',
        status: 'Closed',

        submittedBy: 'JS',
        submittedDate: '2025-09-28',
        reviewedBy: ['WK', 'MC'],
        approvedBy: 'WK',
        approvedDate: '2025-09-30',
        implementedDate: '2025-10-01',
        closedDate: '2025-10-10',

        changeDescription: 'Change switch cover gasket from silicone to TPE (thermoplastic elastomer) grade TPE-S65A, 65 Shore A hardness.',
        reasonForChange: 'FMEA-008 switch gasket degradation (RPN 324). TPE has superior compression set resistance (15% vs silicone 25%) for long-term sealing.',

        affectedDocuments: ['BOM Rev D', 'Material Spec MS-TPE-01', 'IPX4 Test Protocol'],
        affectedItems: ['MF-GSK-001'],
        previousConfiguration: 'Silicone gasket, compression set 25% @ 70h/70°C',
        newConfiguration: 'TPE gasket, compression set 15% @ 22h/70°C, overmoldable with ABS',

        costImpact: {
            tooling: 0,
            material: 0.10,
            labor: 0,
            total: 0.10
        },
        scheduleImpact: '1 week for material switchover',
        qualityImpact: 'Better long-term seal integrity, RPN 324→216',
        validationRequired: true,
        testingRequired: ['IPX4 splash test', 'Compression set test', 'Adhesion to ABS test'],

        linkedFmeaIds: ['FMEA-008'],
        linkedBomIds: ['10'],
        linkedMaterialIds: ['MAT-TPE-001'],
        linkedIterationNumber: 7,

        attachments: ['TPE_Datasheet.pdf', 'Compression_Set_Comparison.pdf', 'IPX4_Test_Pass.pdf'],
        dispositionOfExistingParts: 'Use remaining silicone stock (150 pcs), then switch',

        approvalHistory: [
            {
                reviewer: 'WK',
                role: 'Lead Engineer',
                decision: 'Approved',
                date: '2025-09-30',
                comments: 'TPE is better choice for IPX4 sealing. Approved.'
            },
            {
                reviewer: 'MC',
                role: 'Manufacturing',
                decision: 'Approved',
                date: '2025-09-30',
                comments: 'TPE can be overmolded - future cost savings. Approved.'
            }
        ]
    },

    {
        ecnNumber: 'ECN-2025-007',
        ecnTitle: 'Customer Requirement Update - Housing Length 220mm',
        ecnType: 'Design Change',
        priority: 'Medium',
        status: 'Closed',

        submittedBy: 'WK',
        submittedDate: '2025-10-12',
        reviewedBy: ['MC'],
        approvedBy: 'MC',
        approvedDate: '2025-10-13',
        implementedDate: '2025-10-15',
        closedDate: '2025-10-20',

        changeDescription: 'Increase total frother length from 200mm to 220mm per customer market research feedback. Better ergonomics for tall cups/mugs.',
        reasonForChange: 'Customer feedback: longer handle preferred for deep containers. Market research shows 220mm is optimal length.',

        affectedDocuments: ['Customer Requirements Doc', 'Assembly Drawing', 'BOM Rev D'],
        affectedItems: ['MF-ASSY-001', 'MF-HSG-001'],
        previousConfiguration: 'Overall length 200mm - too short for tall mugs',
        newConfiguration: 'Overall length 220mm - reaches bottom of standard 16oz mug',

        costImpact: {
            tooling: 1200,
            material: 0.05,
            labor: 0,
            total: 1205
        },
        scheduleImpact: '2 weeks for tool modification',
        qualityImpact: 'No quality impact - improved usability',
        validationRequired: false,
        testingRequired: [],

        linkedIterationNumber: 9,

        attachments: ['Customer_Feedback_Summary.pdf', '220mm_Drawing.pdf'],
        dispositionOfExistingParts: 'Sell 200mm version as "compact" model',

        approvalHistory: [
            {
                reviewer: 'MC',
                role: 'Product Manager',
                decision: 'Approved',
                date: '2025-10-13',
                comments: 'Customer feedback is clear. Approve length change.'
            }
        ]
    },

    {
        ecnNumber: 'ECN-2025-008',
        ecnTitle: 'Whisk Diameter Increase - 25mm to 35mm',
        ecnType: 'Design Change',
        priority: 'High',
        status: 'Closed',

        submittedBy: 'WK',
        submittedDate: '2025-10-15',
        reviewedBy: ['MC', 'JS'],
        approvedBy: 'MC',
        approvedDate: '2025-10-17',
        implementedDate: '2025-10-18',
        closedDate: '2025-10-25',

        changeDescription: 'Increase whisk head diameter from 25mm to 35mm for improved aeration performance per customer requirement.',
        reasonForChange: 'Customer testing showed 25mm whisk takes 45sec to froth, 35mm only 30sec. Better foam volume with larger whisk.',

        affectedDocuments: ['Whisk Drawing MF-WHK-001 Rev A→B', 'BOM Rev D', 'Performance Spec'],
        affectedItems: ['MF-WHK-001'],
        previousConfiguration: 'Whisk diameter 25mm, foam time 45sec',
        newConfiguration: 'Whisk diameter 35mm, foam time 30sec - 33% faster',

        costImpact: {
            tooling: 800,
            material: 0.30,
            labor: 0,
            total: 1130
        },
        scheduleImpact: '1 week for tooling, 1 week for validation',
        qualityImpact: 'Performance improvement - faster frothing, better customer satisfaction',
        validationRequired: true,
        testingRequired: ['Froth performance test', 'Foam volume measurement', 'Motor torque check'],

        linkedFmeaIds: ['FMEA-014'],
        linkedBomIds: ['14'],
        linkedIterationNumber: 9,

        attachments: ['Whisk_Performance_Test.pdf', '35mm_Drawing.pdf', 'Customer_Froth_Test.pdf'],
        relatedECNs: ['ECN-2025-007'],
        dispositionOfExistingParts: 'Use 25mm stock for economy model',

        approvalHistory: [
            {
                reviewer: 'MC',
                role: 'Product Manager',
                decision: 'Approved',
                date: '2025-10-17',
                comments: 'Performance data is compelling. Approve 35mm whisk.'
            },
            {
                reviewer: 'JS',
                role: 'Quality',
                decision: 'Approved',
                date: '2025-10-17',
                comments: 'Need to verify motor torque capacity. Conditional on motor test.'
            }
        ]
    },

    {
        ecnNumber: 'ECN-2025-009',
        ecnTitle: 'Motor Bearing Upgrade - Steel to Ceramic',
        ecnType: 'Material Change',
        priority: 'Medium',
        status: 'Closed',

        submittedBy: 'WK',
        submittedDate: '2025-10-20',
        reviewedBy: ['MC'],
        approvedBy: 'MC',
        approvedDate: '2025-10-21',
        implementedDate: '2025-10-22',
        closedDate: '2025-11-01',

        changeDescription: 'Upgrade motor bearings from steel ball bearings to silicon nitride ceramic bearings for noise reduction.',
        reasonForChange: 'Customer complaints: motor noise 72 dB exceeds requirement (70 dB limit). Ceramic bearings reduce noise to 55 dB - 40% quieter.',

        affectedDocuments: ['Motor Spec', 'BOM Rev D', 'Noise Test Protocol'],
        affectedItems: ['MF-BRG-001'],
        previousConfiguration: 'Steel ball bearings, noise 72 dB @ 1m',
        newConfiguration: 'Ceramic (Si3N4) bearings, noise 55 dB @ 1m - meets spec',

        costImpact: {
            tooling: 0,
            material: 2.50,
            labor: 0,
            total: 2.50
        },
        scheduleImpact: 'None - motor supplier stocks ceramic option',
        qualityImpact: 'Noise reduced 40%, meets customer requirement <70 dB',
        validationRequired: true,
        testingRequired: ['Noise measurement per ISO 3744', 'Vibration test', 'Life test'],

        linkedFmeaIds: ['FMEA-010'],
        linkedBomIds: ['7'],
        linkedMaterialIds: ['MAT-CERAMIC-001'],
        linkedIterationNumber: 11,

        attachments: ['Noise_Test_Before.pdf', 'Noise_Test_After.pdf', 'Ceramic_Bearing_Spec.pdf'],
        dispositionOfExistingParts: 'Return steel bearing motors to supplier for credit',

        approvalHistory: [
            {
                reviewer: 'MC',
                role: 'Product Manager',
                decision: 'Approved',
                date: '2025-10-21',
                comments: 'Customer requirement critical. Worth the cost increase.'
            }
        ]
    },

    {
        ecnNumber: 'ECN-2025-010',
        ecnTitle: 'Dual Seal Design - Add NBR Backup Seal',
        ecnType: 'Design Change',
        priority: 'Critical',
        status: 'Closed',

        submittedBy: 'WK',
        submittedDate: '2025-10-28',
        reviewedBy: ['JS', 'MC'],
        approvedBy: 'JS',
        approvedDate: '2025-10-29',
        implementedDate: '2025-10-30',
        closedDate: '2025-11-05',

        changeDescription: 'Add secondary NBR (nitrile rubber) O-ring as backup seal behind primary Viton seal. Dual seal redundancy for critical function.',
        reasonForChange: 'CRITICAL: Shaft seal is single point of failure (FMEA-016, S=9). If primary seal leaks, liquid enters motor = total failure. Redundancy required.',

        affectedDocuments: ['Whisk Assy Drawing', 'BOM Rev D→E', 'DFMEA Rev 3→4', 'Seal Test Protocol'],
        affectedItems: ['MF-WHISK-ASSY-002', 'New part: MF-SEL-NBR-001'],
        previousConfiguration: 'Single Viton seal - no redundancy',
        newConfiguration: 'Dual seal: Viton FKM primary + NBR backup - redundant protection',

        costImpact: {
            tooling: 200,
            material: 0.15,
            labor: 0.05,
            total: 220
        },
        scheduleImpact: '1 week for design, 1 week for testing',
        qualityImpact: 'MAJOR reliability improvement. Seal leak probability reduced 60%. RPN 162→108',
        validationRequired: true,
        testingRequired: ['Dual seal leak test', 'Hot liquid test', '1000-cycle endurance test'],

        linkedFmeaIds: ['FMEA-016'],
        linkedBomIds: ['15', '16', '17'],
        linkedMaterialIds: ['MAT-VITON-001', 'MAT-NBR-001'],
        linkedIterationNumber: 10,

        attachments: ['Dual_Seal_Design.pdf', 'Redundancy_Analysis.pdf', 'Leak_Test_Results.pdf'],
        relatedECNs: ['ECN-2025-005'],
        dispositionOfExistingParts: 'Upgrade existing single-seal units with backup seal retrofit kit',

        approvalHistory: [
            {
                reviewer: 'JS',
                role: 'Quality/Reliability',
                decision: 'Approved',
                date: '2025-10-29',
                comments: 'CRITICAL improvement. Eliminates single point of failure. MUST implement.'
            },
            {
                reviewer: 'MC',
                role: 'Manufacturing',
                decision: 'Approved',
                date: '2025-10-29',
                comments: 'Minor assembly change. Cost justified by reliability gain.'
            }
        ]
    },

    {
        ecnNumber: 'ECN-2025-011',
        ecnTitle: 'Motor Technology Upgrade - Brushed to Brushless DC',
        ecnType: 'Design Change',
        priority: 'Critical',
        status: 'Closed',

        submittedBy: 'WK',
        submittedDate: '2025-10-22',
        reviewedBy: ['JS', 'MC'],
        approvedBy: 'MC',
        approvedDate: '2025-10-24',
        implementedDate: '2025-10-25',
        closedDate: '2025-11-05',

        changeDescription: 'Replace brushed DC motor with brushless DC (BLDC) motor. Speed increase from 12,000 to 15,000 RPM. Integrated ceramic bearings.',
        reasonForChange: 'MAJOR UPGRADE: (1) Eliminate brush wear failure mode (FMEA-011 RPN 72→18 = 75% reduction), (2) Increase power for 35mm whisk, (3) Longer life',

        affectedDocuments: ['Motor Spec', 'Electrical Schematic', 'BOM Rev E→F', 'DFMEA Rev 4→5'],
        affectedItems: ['MF-MOT-001 obsolete → MF-MOT-HC-002 new'],
        previousConfiguration: 'Brushed DC motor 12,000 RPM, brushes wear out @ 100h',
        newConfiguration: 'Brushless DC motor 15,000 RPM, no brushes, 200h+ life',

        costImpact: {
            tooling: 0,
            material: 3.80,
            labor: 0,
            total: 3.80
        },
        scheduleImpact: '2 weeks for supplier qualification (TurboMotors Ltd)',
        qualityImpact: 'MAJOR: Eliminates failure mode, increases performance and life. RPN reduction 54 points.',
        validationRequired: true,
        testingRequired: ['Motor life test 200h', 'Performance test 15k RPM', 'EMC test', 'Thermal test'],

        linkedFmeaIds: ['FMEA-009', 'FMEA-010', 'FMEA-011'],
        linkedBomIds: ['6', '7'],
        linkedIterationNumber: 12,

        attachments: ['Brushless_Motor_Spec.pdf', 'Life_Test_200h_Pass.pdf', 'Performance_Comparison.pdf', 'TurboMotors_Qualification.pdf'],
        relatedECNs: ['ECN-2025-009'],
        dispositionOfExistingParts: 'Return all brushed motors to supplier (350 units) for credit',

        approvalHistory: [
            {
                reviewer: 'MC',
                role: 'Product Manager',
                decision: 'Approved',
                date: '2025-10-24',
                comments: 'Game-changer. Cost increase justified by performance & reliability. Approve as PREMIUM model.'
            },
            {
                reviewer: 'JS',
                role: 'Quality',

                decision: 'Approved',
                date: '2025-10-24',
                comments: 'Eliminates brush failure mode completely. Major quality improvement. Approved.'
            }
        ]
    },

    {
        ecnNumber: 'ECN-2025-012',
        ecnTitle: 'Shaft-Motor Press-Fit Assembly Procedure',
        ecnType: 'Process Change',
        priority: 'High',
        status: 'Closed',

        submittedBy: 'JS',
        submittedDate: '2025-11-18',
        reviewedBy: ['WK', 'MC'],
        approvedBy: 'WK',
        approvedDate: '2025-11-19',
        implementedDate: '2025-11-20',
        closedDate: '2025-11-25',

        changeDescription: 'Develop and document controlled press-fit procedure for shaft-to-motor coupling. Press force 500-800N with inline force monitoring.',
        reasonForChange: 'FMEA-013 shaft slip risk (RPN 144). Need controlled assembly process to ensure reliable coupling. Prevent motor spin without whisk rotation.',

        affectedDocuments: ['Work Instruction WI-ASM-005', 'BOM Rev F', 'PFMEA'],
        affectedItems: ['MF-SFT-001', 'MF-MOT-HC-002'],
        previousConfiguration: 'Manual press-fit, no force control, inconsistent results',
        newConfiguration: 'Pneumatic press with 500-800N control, 100% inline force verification',

        costImpact: {
            tooling: 2500,
            material: 0,
            labor: 0.10,
            total: 2610
        },
        scheduleImpact: '1 week for press equipment setup',
        qualityImpact: 'Eliminates press-fit variation, ensures reliable shaft coupling',
        validationRequired: true,
        testingRequired: ['Pull-out force test 30 samples', 'Torque test', 'Process capability study Cpk >1.33'],

        linkedFmeaIds: ['FMEA-013'],
        linkedBomIds: ['13'],
        linkedIterationNumber: 15,

        attachments: ['WI-ASM-005_Press_Fit.pdf', 'Process_Capability_Study.pdf', 'Press_Equipment_Spec.pdf'],
        dispositionOfExistingParts: 'N/A - process change only',

        approvalHistory: [
            {
                reviewer: 'WK',
                role: 'Engineering',
                decision: 'Approved',
                date: '2025-11-19',
                comments: 'Critical process control. Press equipment investment justified.'
            },
            {
                reviewer: 'MC',
                role: 'Manufacturing',
                decision: 'Approved',
                date: '2025-11-19',
                comments: 'Will implement with training. Cpk target achievable.'
            }
        ]
    },

    {
        ecnNumber: 'ECN-2025-013',
        ecnTitle: 'End-of-Line Test Protocol - Electrical & Motor',
        ecnType: 'Process Change',
        priority: 'High',
        status: 'Closed',

        submittedBy: 'MC',
        submittedDate: '2025-11-20',
        reviewedBy: ['WK', 'JS'],
        approvedBy: 'WK',
        approvedDate: '2025-11-21',
        implementedDate: '2025-11-22',
        closedDate: '2025-11-28',

        changeDescription: '100% EOL testing protocol: (1) Electrical continuity <1Ω, (2) Motor speed 14,500-15,500 RPM, (3) No-load current <0.4A, (4) Stall current <2.5A',
        reasonForChange: 'Production quality assurance. Catch assembly defects before shipping. Industry best practice for electrical products.',

        affectedDocuments: ['Test Protocol TP-EOL-001', 'Quality Plan', 'Manufacturing Routing'],
        affectedItems: ['ALL - Final Assembly'],
        previousConfiguration: 'Sample testing only (10%), defects found in field',
        newConfiguration: '100% automated EOL test, auto-tester rejects failures',

        costImpact: {
            tooling: 12000,
            material: 0,
            labor: 0.15,
            total: 12015
        },
        scheduleImpact: '30sec/unit test time added to production',
        qualityImpact: 'Zero defect goal - catch 100% of electrical/motor failures before ship',
        validationRequired: true,
        testingRequired: ['EOL tester validation', 'GR&R study', 'Known-good/known-bad test'],

        linkedIterationNumber: 15,

        attachments: ['TP-EOL-001_Protocol.pdf', 'Auto_Tester_Spec.pdf', 'GRR_Study.pdf'],
        dispositionOfExistingParts: 'Test all existing inventory before release',

        approvalHistory: [
            {
                reviewer: 'WK',
                role: 'Engineering',
                decision: 'Approved',
                date: '2025-11-21',
                comments: 'Essential for quality. Tester investment approved.'
            },
            {
                reviewer: 'JS',
                role: 'Quality',
                decision: 'Approved',
                date: '2025-11-21',
                comments: '100% test is industry standard. Must implement.'
            }
        ]
    },

    {
        ecnNumber: 'ECN-2025-014',
        ecnTitle: 'Product Label with CE/UL Compliance Markings',
        ecnType: 'Documentation',
        priority: 'Critical',
        status: 'Closed',

        submittedBy: 'MC',
        submittedDate: '2025-11-28',
        reviewedBy: ['WK'],
        approvedBy: 'WK',
        approvedDate: '2025-11-29',
        implementedDate: '2025-12-01',
        closedDate: '2025-12-05',

        changeDescription: 'Add regulatory compliance label with CE mark, UL listing, model number AERATOR-MF-2025-V17, serial number, safety warnings.',
        reasonForChange: 'MANDATORY for EU (CE) and US (UL) market entry. Regulatory compliance requirement.',

        affectedDocuments: ['Label Artwork', 'BOM Rev F', 'Compliance Documentation'],
        affectedItems: ['MF-LABEL-001'],
        previousConfiguration: 'No compliance markings',
        newConfiguration: 'UV-resistant label with CE, UL, part number, serial, safety icons',

        costImpact: {
            tooling: 500,
            material: 0.10,
            labor: 0,
            total: 610
        },
        scheduleImpact: '1 week for label artwork approval',
        qualityImpact: 'Enables market entry to EU and USA',
        validationRequired: true,
        testingRequired: ['Label adhesion test ASTM D3359', 'UV resistance 100h'],

        linkedFmeaIds: ['FMEA-020'],
        linkedBomIds: ['20'],
        linkedIterationNumber: 16,

        attachments: ['Label_Artwork_Final.pdf', 'CE_Declaration.pdf', 'UL_Certificate.pdf', 'Adhesion_Test.pdf'],
        dispositionOfExistingParts: 'Apply labels to unlabeled inventory',

        approvalHistory: [
            {
                reviewer: 'WK',
                role: 'Engineering',
                decision: 'Approved',
                date: '2025-11-29',
                comments: 'Label artwork reviewed and approved. Proceed to production.'
            }
        ]
    },

    {
        ecnNumber: 'ECN-2025-015',
        ecnTitle: 'Design Freeze - Production Release V1.0',
        ecnType: 'Documentation',
        priority: 'Critical',
        status: 'Closed',

        submittedBy: 'WK',
        submittedDate: '2025-12-10',
        reviewedBy: ['JS', 'MC'],
        approvedBy: 'MC',
        approvedDate: '2025-12-11',
        implementedDate: '2025-12-12',
        closedDate: '2025-12-12',

        changeDescription: 'DESIGN FREEZE: Release Aerator Pro V1.0 to full production. All validation complete, pilot build successful (50 units, zero defects).',
        reasonForChange: 'All requirements met: (1) RPN reduced 31% from baseline, (2) All tests passed (Drop, IPX4, Life, FDA), (3) Pilot build 100% pass rate',

        affectedDocuments: ['ALL - Master BOM Rev F FROZEN', 'DFMEA Rev 5 FROZEN', 'All Drawings FROZEN'],
        affectedItems: ['AERATOR-MF-2025-V17'],
        previousConfiguration: 'Development/validation phase',
        newConfiguration: 'PRODUCTION RELEASE - Design frozen, configuration controlled',

        costImpact: {
            tooling: 0,
            material: 0,
            labor: 0,
            total: 0
        },
        scheduleImpact: 'N/A - Release to production',
        qualityImpact: 'FINAL QUALITY: Total RPN 1289 (31% reduction), 20/20 components validated, all tests passed',
        validationRequired: false,
        testingRequired: [],

        linkedIterationNumber: 17,

        attachments: [
            'Final_BOM_Rev_F.pdf',
            'Final_DFMEA_Rev_5.pdf',
            'Pilot_Build_Report.pdf',
            'Validation_Summary.pdf',
            'Production_Release_Package.pdf'
        ],
        dispositionOfExistingParts: 'All existing parts approved for production use',

        approvalHistory: [
            {
                reviewer: 'MC',
                role: 'Product Manager',
                decision: 'Approved',
                date: '2025-12-11',
                comments: '🎉 PRODUCTION APPROVED! Excellent work team. Ready for market launch.'
            },
            {
                reviewer: 'JS',
                role: 'Quality',
                decision: 'Approved',
                date: '2025-12-11',
                comments: 'All quality gates passed. Design freeze approved. Release to production.'
            },
            {
                reviewer: 'WK',
                role: 'Engineering',
                decision: 'Approved',
                date: '2025-12-11',
                comments: 'Design validated and frozen. Configuration management in place.'
            }
        ]
    }
];

// ECN Summary Statistics
export const ecnSummary = {
    totalECNs: ecnLog.length,
    byType: {
        'Design Change': ecnLog.filter(e => e.ecnType === 'Design Change').length,
        'Material Change': ecnLog.filter(e => e.ecnType === 'Material Change').length,
        'Process Change': ecnLog.filter(e => e.ecnType === 'Process Change').length,
        'Supplier Change': ecnLog.filter(e => e.ecnType === 'Supplier Change').length,
        'Documentation': ecnLog.filter(e => e.ecnType === 'Documentation').length
    },
    byPriority: {
        'Critical': ecnLog.filter(e => e.priority === 'Critical').length,
        'High': ecnLog.filter(e => e.priority === 'High').length,
        'Medium': ecnLog.filter(e => e.priority === 'Medium').length,
        'Low': ecnLog.filter(e => e.priority === 'Low').length
    },
    byStatus: {
        'Closed': ecnLog.filter(e => e.status === 'Closed').length
    },
    totalCostImpact: ecnLog.reduce((sum, e) => sum + e.costImpact.total, 0),
    totalToolingCost: ecnLog.reduce((sum, e) => sum + e.costImpact.tooling, 0),
    totalMaterialCost: ecnLog.reduce((sum, e) => sum + e.costImpact.material, 0),
    contributors: {
        'WK': ecnLog.filter(e => e.submittedBy === 'WK').length,
        'JS': ecnLog.filter(e => e.submittedBy === 'JS').length,
        'MC': ecnLog.filter(e => e.submittedBy === 'MC').length
    },
    approvers: {
        'WK': ecnLog.filter(e => e.approvedBy === 'WK').length,
        'JS': ecnLog.filter(e => e.approvedBy === 'JS').length,
        'MC': ecnLog.filter(e => e.approvedBy === 'MC').length
    },
    criticalECNs: [
        'ECN-2025-003 (FDA Compliance)',
        'ECN-2025-005 (Seal Failure Fix)',
        'ECN-2025-010 (Dual Seal Redundancy)',
        'ECN-2025-011 (Brushless Motor)',
        'ECN-2025-015 (Production Release)'
    ],
    developmentPeriod: '132 days (Aug 1 - Dec 12, 2025)'
};

export const ecnLogInfo = {
    totalECNs: ecnLog.length,
    allClosed: true,
    generatedDate: '2025-12-12',
    projectName: 'Handheld Milk Frother - Aerator Pro V1.0',
    finalStatus: 'Released to Production'
};
